import { ConfigProvider, Empty, Skeleton, Table, TableProps } from 'antd';
import { AnyObject } from 'antd/es/_util/type';
import './style.scss';

interface TableComponentProps<T extends AnyObject> extends TableProps<T> {
  tableHeader: TableProps<T>['columns'];
  tableColumns: TableProps<T>['dataSource'];
  highlighted?: string | number | null;
  setHighlighted?: (record: T) => boolean;
  highlightColor?: 'red' | 'gray';
}

const TableComponent = <T extends AnyObject>({
  loading,
  tableHeader,
  tableColumns,
  pagination,
  highlighted,
  setHighlighted,
  highlightColor,
  ...props
}: TableComponentProps<T>) => {
  const defaultPagination = {
    showSizeChanger: true,
    hideOnSinglePage: false,
    pageSizeOptions: ['10', '20', '30'],
  };

  const getScrollX = () => {
    const headerLength = tableHeader?.length;
    if (headerLength && headerLength >= 11) {
      return 2000;
    } else if (headerLength && headerLength >= 9) {
      return 1800;
    } else if (headerLength && headerLength > 5) {
      return 1500;
    } else {
      return 800;
    }
  };

  // Create skeleton data that matches your table structure
  const createSkeletonData = () => {
    const skeletonRows = 8; // Number of skeleton rows to show
    const skeletonColumns = tableHeader?.map((col, index) => ({
      ...col,
      render: () => (
        <Skeleton.Input
          active
          size="small"
          style={{
            width: index === 0 ? '60%' : index % 3 === 0 ? '80%' : '70%',
            height: '16px',
          }}
        />
      ),
    }));

    const skeletonDataSource = Array.from(
      { length: skeletonRows },
      (_, index) => ({
        key: `skeleton-${index}`,
        id: `skeleton-${index}`,
      }),
    );

    return { skeletonColumns, skeletonDataSource };
  };

  const renderEmpty = () => {
    if (loading) return <div className="table-loader-placeholder" />;
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />;
  };

  // If loading, show skeleton table
  if (loading) {
    const { skeletonColumns, skeletonDataSource } = createSkeletonData();

    return (
      <div className="table-skeleton-wrapper">
        <ConfigProvider renderEmpty={renderEmpty}>
          <Table
            className="table-wrapper table-skeleton"
            columns={skeletonColumns}
            dataSource={skeletonDataSource as unknown as T[]}
            sticky={{
              offsetHeader: -48,
            }}
            pagination={false}
            showHeader={true}
          />
        </ConfigProvider>
      </div>
    );
  }

  return (
    <ConfigProvider renderEmpty={renderEmpty}>
      <Table
        rowClassName={(record, index) => {
          let className = '';
          if (index === (tableColumns?.length || 0) - 1) {
            className += ' mb-3';
          }
          if (
            (highlighted && record.id === highlighted) ||
            (setHighlighted && setHighlighted(record))
          ) {
            return (
              'table-highlight' +
              (highlightColor ? ` table-highlight__${highlightColor}` : '') +
              className
            );
          }
          return className;
        }}
        className="table-wrapper"
        columns={tableHeader}
        dataSource={tableColumns}
        scroll={props?.scroll ? props?.scroll : { x: getScrollX() }}
        sticky={{
          offsetHeader: -48,
        }}
        pagination={
          pagination === false ? false : { ...pagination, ...defaultPagination }
        }
        {...props}
      />
    </ConfigProvider>
  );
};

export default TableComponent;
